import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';

import { DfService } from '../../../../core/df/df.service';
import { DfFacilityOps, DfStatus, DF_STATUS_META } from '../../../../core/df/df.types';
import { aiRecommendationLabel } from '../../../../core/status/status-meta.types';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { IconComponent } from '../../../../shared/icon/icon';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';

@Component({
  selector: 'app-df-facility-queue',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    RouterLink,
    DatePipe,
    StatusChipComponent,
    IconComponent,
    ZarPipe,
  ],
  templateUrl: './df-facility-queue.html',
  styleUrl: './df-facility-queue.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfFacilityQueueComponent implements OnInit {
  private readonly dfService = inject(DfService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Rendered inside the Ops Applications page (FLOW-346), which owns the page
   * head and breadcrumb; this list then shows only its count and filter chip.
   * Standalone at /ops/df/facilities it keeps its own head.
   */
  readonly embedded = input(false);

  @ViewChild(MatPaginator) set paginator(p: MatPaginator) {
    if (p) this.dataSource.paginator = p;
  }

  protected readonly dataSource = new MatTableDataSource<DfFacilityOps>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  /** Optional ?status= filter from the URL (e.g. linked from the admin dashboard). */
  protected readonly statusFilter = signal<DfStatus | null>(null);

  protected get facilityCount(): number {
    return this.dataSource.filteredData.length;
  }

  protected readonly statusFilterLabel = signal<string | null>(null);

  protected readonly pageSizeOptions = [25, 50, 100];

  protected readonly columns = [
    'entity',
    'requested_limit',
    'ai_recommendation',
    'stage',
    'submitted',
    'assignee',
  ];

  ngOnInit(): void {
    // Filter the MatTableDataSource by df_status; the stored filter string is
    // the active status (or '' for no filter).
    this.dataSource.filterPredicate = (row, filter) => !filter || row.df_status === filter;

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const status = params.get('status') as DfStatus | null;
      const valid = status && status in DF_STATUS_META ? status : null;
      this.statusFilter.set(valid);
      this.statusFilterLabel.set(valid ? DF_STATUS_META[valid].label : null);
      this.dataSource.filter = valid ?? '';
      if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
      this.cdr.markForCheck();
    });

    this.load();
  }

  protected readonly aiRecLabel = aiRecommendationLabel;

  protected clearFilter(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status: null },
      queryParamsHandling: 'merge',
    });
  }

  protected load(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.dfService
      .opsList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.dataSource.data = res.facilities;
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
